import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { isEnterKey } from '@peloton/keyboard';
import { ellipsis, defaultTransition, hoverTransition } from '@peloton/styles';
import { white, gray2, gray4, slate3, slate1 } from '@engage/colors';
import { spaces } from '@engage/styles';
import { label16 } from '@engage/typography';
import {
  useClasses,
  Classes,
  Cta,
  useCta,
  ClassSearch,
  ClassSearchCopy,
} from '@members/copy';
import { CloseIcon } from '@members/icons';
import type { SupportedQueryParams } from '@members/pg-search/context';
import { mergeQueryParams, SearchType } from '@members/pg-search/context';
import { SEARCH_TERMS } from '@members/pg-search/SearchInput';
import { SearchSparkle } from '@members/pg-search/SearchSparkle';
import { SEARCH_ROUTE } from '@members/pg-search/urls';
import SearchIcon from './icons/SearchIcon';
import { getLibraryPageCategory } from './urls';

const ANALYTICS_SOURCE = 'Search Bar';

const getQueryString = ({ query, pathname }: { query: string; pathname: string }) => {
  const activeBrowseCategorySlug = getLibraryPageCategory(pathname);
  const supportedState: SupportedQueryParams = {
    query,
    fromCategory: activeBrowseCategorySlug,
    searchType: SearchType.ManualSearch,
  };
  return mergeQueryParams(supportedState);
};

export const SearchBar: React.FC<React.PropsWithChildren<unknown>> = () => {
  const [localQuery, setLocalQuery] = useState('');
  const isEmpty = !localQuery;

  const searchBarLabel = useClasses(Classes.SearchBarLabel);
  const clearButtonLabel = useCta(Cta.InputClear);

  const location = useLocation();
  const history = useHistory();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setLocalQuery(event.currentTarget.value);
  const handleOnClick = () => setLocalQuery('');
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isEnterKey(event)) {
      history.replace(
        `${SEARCH_ROUTE}?${getQueryString({
          query: localQuery,
          pathname: location.pathname,
        })}`,
        {
          source: ANALYTICS_SOURCE,
          state: {
            from: history.location.pathname,
          },
        },
      );
    }
  };

  return (
    <InputContainer isEmpty={isEmpty}>
      <Input
        autoComplete="off"
        name="searchTerm"
        type="text"
        value={localQuery}
        onClick={event => {
          event.preventDefault();
          history.push('/search?c=all', {
            source: ANALYTICS_SOURCE,
            state: {
              from: history.location.pathname,
            },
          });
        }}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        aria-label={searchBarLabel}
        data-test-id="searchBar"
      />
      <StyledLabel isEmpty={isEmpty} htmlFor={searchBarLabel}>
        <span style={{ marginRight: '8px' }}>
          <SearchIcon />
        </span>
        <ClassSearchCopy id={ClassSearch.LinkTitle} />{' '}
        <SearchSparkle searchTerms={SEARCH_TERMS} />
      </StyledLabel>
      <ClearButtonContainer>
        <ClearButton onClick={handleOnClick} aria-label={clearButtonLabel}>
          <StyledCloseIcon />
        </ClearButton>
      </ClearButtonContainer>
    </InputContainer>
  );
};

const Input = styled.input`
  ${label16}
  background-color: ${white};
  outline: none;
  border: none;
  width: 100%;
  height: ${spaces.xHuge}px;
  color: ${slate3};
  border-radius: 999px;
  padding: 0 ${spaces.xHuge}px;
`;

const StyledLabel = styled.label<{ isEmpty: boolean }>`
  ${label16}
  color: ${slate1};
  position: absolute;
  pointer-events: none;
  left: 0;
  top: 0;
  ${defaultTransition('all', 200)}
  height: 100%;
  width: 100%;
  text-align: left;
  align-items: center;
  display: flex;
  margin-left: ${spaces.large}px;
  padding-right: ${spaces.medium}px;
  & > div {
    margin: 0 ${spaces.small}px;
    opacity: ${props => (props.isEmpty ? '1' : '0')};
    width: 100%;
    ${ellipsis}
  }
`;
const ClearButtonContainer = styled.div`
  position: absolute;
  right: 0px;
  top: 0px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const ClearButton = styled.button`
  ${defaultTransition('opacity')}
  cursor: pointer;
  fill: ${gray4};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  height: ${spaces.xxLarge}px;
  width: ${spaces.xxLarge}px;
  border-radius: 50%;
  ${hoverTransition({
    property: 'background',
    to: gray2,
  })}
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: ${spaces.small}px;
  height: ${spaces.small}px;
  fill: ${slate3};
`;
const InputContainer = styled.div<{ isEmpty: boolean }>`
  ${label16}
  position: relative;
  flex: 1;
  height: ${spaces.xHuge}px;

  ${ClearButton} {
    margin-right: ${spaces.medium}px;
    opacity: ${props => (props.isEmpty ? '0' : '1')};
    visibility: ${props => (props.isEmpty ? 'hidden' : 'visible')};
  }
`;
